import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  styled,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Fade,
  Backdrop,
  CircularProgress,
} from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import defaultImage from '../../Assets/images/image2.png';
import { useNavigate } from 'react-router-dom';
import { del } from '../../utility/fetchWeb';
import notification from '../../utility/notification';

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
}));

const ImageWrapper = styled('div')(() => ({
  width: '100%',
  aspectRatio: '16 / 9',
  position: 'relative',
  overflow: 'hidden',
  backgroundColor: '#f5f5f5',
}));

const StyledImage = styled('img')({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
});


const EllipsisTypography = styled(Typography)(({ lines = 1 }) => ({
  display: '-webkit-box',
  WebkitLineClamp: lines,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  lineHeight: '1.5', // Improve readability
}));

const ModernMoreButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(10px)',
  borderRadius: '50%',
  width: 32,
  height: 32,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
  border: '1px solid rgba(255, 255, 255, 0.3)',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 1)',
    transform: 'scale(1.05)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
  },
  '& .MuiSvgIcon-root': {
    fontSize: '18px',
    color: theme.palette.text.primary,
  }
}));

const StyledMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 12,
    minWidth: 128, // Reduced from 160 to 128 (20% reduction)
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
    border: '1px solid rgba(0, 0, 0, 0.08)',
    '& .MuiMenuItem-root': {
      padding: '12px 16px',
      fontSize: '14px',
      fontWeight: 500,
      borderRadius: 8,
      margin: '4px 8px',
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.04)',
        transform: 'translateX(2px)',
      },
      '& .MuiSvgIcon-root': {
        fontSize: '18px',
        marginRight: '8px',
      }
    }
  }
}));

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: 16,
    padding: theme.spacing(1),
    boxShadow: '0 24px 48px rgba(0, 0, 0, 0.2)',
  }
}));

const CommunityCard = ({ 
  id, 
  imageUrl, 
  title, 
  subtitle, 
  author, 
  time, 
  userId, 
  onEdit, 
  onDelete 
}) => {
  const navigate = useNavigate();
  const currentUserId = parseInt(sessionStorage.getItem('userId'));
  const isOwner = userId === currentUserId;

  const [anchorEl, setAnchorEl] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleMenuClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = (event) => {
    event.stopPropagation();
    handleMenuClose();
    if (onEdit) {
      onEdit(id);
    }
  };

  const handleDeleteClick = (event) => {
    event.stopPropagation();
    handleMenuClose();
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      const response = await del(`/Articles/${id}`);
      notification({ message: 'Article deleted successfully', type: 'success' });
      if (onDelete) {
        onDelete(id);
      }
    } catch (error) {
      console.error('Error deleting article:', error);
      notification({ message: 'Failed to delete article', type: 'error' });
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  const handleDeleteCancel = () => {
    if (!isDeleting) {
      setDeleteDialogOpen(false);
    }
  };

  const handleCardClick = () => {
    navigate(`/articles/${id}`);
  };

  function stripHtml(html) {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  }

  return (
    <>
      <StyledCard
        onClick={handleCardClick}
        sx={{
          cursor: 'pointer',
          '&:hover': {
            boxShadow: 6,
            transform: 'translateY(-2px)',
            transition: 'all 0.2s ease-in-out'
          }
        }}
      >
        <ImageWrapper>
          <StyledImage
            src={imageUrl ? `${process.env.REACT_APP_MEDIA_URL}${imageUrl}` : defaultImage}
            alt={title}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = defaultImage;
            }}
          />
          {isOwner && (
            <Box
              sx={{
                position: 'absolute',
                top: 12,
                right: 12,
              }}
            >
              <ModernMoreButton
                size="small"
                onClick={handleMenuClick}
              >
                <MoreVertIcon />
              </ModernMoreButton>
            </Box>
          )}
        </ImageWrapper>

        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <EllipsisTypography variant="subtitle1" fontWeight="bold" lines={2}>
            {title}
          </EllipsisTypography>
          <EllipsisTypography
            variant="body2"
            color="text.secondary"
            lines={1}
            sx={{ minHeight: '24px', mb: 1 }}
          >
            {stripHtml(subtitle).slice(0, 100)}...
          </EllipsisTypography>

          <Typography variant="caption" color="text.secondary">{author}</Typography>

          <Box mt="auto" display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={0.5}>
              <AccessTimeIcon fontSize="small" />
              <Typography variant="caption">{time}</Typography>
            </Box>
          </Box>
        </CardContent>
      </StyledCard>

      {/* Modern Menu */}
      <StyledMenu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
        TransitionComponent={Fade}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleEdit}>
          <EditIcon fontSize="small" />
          Edit Article
        </MenuItem>
        <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
          <DeleteIcon fontSize="small" />
          Delete Article
        </MenuItem>
      </StyledMenu>

      {/* Modern Delete Confirmation Dialog */}
      <StyledDialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        onClick={(e) => e.stopPropagation()}
        BackdropComponent={Backdrop}
        BackdropProps={{
          style: { backgroundColor: 'rgba(0, 0, 0, 0.5)' }
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, pb: 1 }}>
          Delete Article
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 1 }}>
            Are you sure you want to delete "<strong>{title}</strong>"?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This action cannot be undone and will permanently remove the article.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={handleDeleteCancel} 
            disabled={isDeleting}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            disabled={isDeleting}
            variant="contained"
            sx={{ borderRadius: 2, minWidth: 120 }}
            startIcon={isDeleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </StyledDialog>
    </>
  );
};

export default CommunityCard;
